import styled from '@emotion/styled'
import { colors } from '@theme/variants/MolsonTheme'
import FileUploader, {
  type IFileUploaderOptions
} from 'devextreme-react/file-uploader'
import ProgressBar from 'devextreme-react/progress-bar'
import { forwardRef } from 'react'
import { MolsonIcon } from '../molson-icons'

const UploadBox = styled('div')<{ previewImageSize: ImageProperties }>(
  ({ previewImageSize }) => ({
    '.molson-dx-fu-progressbar-wrapper': {
      position: 'relative',

      '.dx-button': {
        '&:hover, &:active, &:focus': {
          backgroundColor: 'transparent'
        }
      },

      '.image-preview': {
        maxHeight: previewImageSize.maxHeight,
        maxWidth: previewImageSize.maxWidth,
        marginTop: 20
      },

      '& .info-text': {
        position: 'absolute',
        bottom: 25,
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '18px',
        color: colors.secondary[600]
      },

      '& .dx-state-disabled': {
        '& .dx-fileuploader-input-label::before': {
          position: 'static',
          content: '"or drag and drop"'
        }
      },

      '& .rejected-file': {
        '& .dx-fileuploader-file-container': {
          border: '1px solid #DC0C00'
        }
      },

      '& .dx-fileuploader-file-info.custom-file-size': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',

        '& .dx-fileuploader-file-size': {
          display: 'none'
        },

        // Injecting custom file size
        '&::after': {
          content: 'attr(data-file-size)',
          display: 'block',
          fontSize: '10px',
          marginTop: '-3px',
          marginLeft: '10px',
          color: 'rgba(102, 102, 102, 0.87)'
        }
      },

      // Custom error styling for files exceeding total size limit
      '& .dx-fileuploader-file-container.file-size-error': {
        // hiding the default "Uploaded" text when container has the error class
        '& .dx-fileuploader-file-status-message': {
          display: 'none'
        },

        // Injecting custom error message
        '&::after': {
          content: 'attr(data-error-message)',
          display: 'block',
          fontSize: '12px',
          marginLeft: '40px',
          marginTop: '-10px',
          color: '#DC0C00'
        }
      }
    }
  })
)

type ImageProperties = {
  maxWidth: number
  maxHeight: number
}

export interface IMolsonFileUploader extends IFileUploaderOptions {
  imageSource?: ArrayBuffer | null | string
  infoText?: string
  progressVisible?: boolean
  progressValue?: number
  previewImageSize?: ImageProperties
  dataTestId?: string
  disabled?: boolean
}

export const MolsonFileUploader = forwardRef(function MolsonFileUploader(
  {
    infoText = '',
    imageSource,
    progressVisible,
    progressValue,
    previewImageSize = {
      maxHeight: 150,
      maxWidth: 250
    },
    dataTestId,
    disabled = false,
    selectButtonText = 'Click to upload',
    labelText = 'or drag and drop',
    uploadMode = 'useButtons',
    ...props
  }: IMolsonFileUploader,
  ref: React.LegacyRef<FileUploader> | undefined
) {
  return (
    <UploadBox previewImageSize={previewImageSize}>
      <div className="molson-dx-fu-progressbar-wrapper">
        <div className="molson-dx-file-uploader">
          {imageSource ? (
            <img
              className="image-preview"
              alt="preview"
              src={imageSource as string}
            />
          ) : (
            <MolsonIcon variant="FILE_UPLOAD" sx={{ fontSize: 40 }} />
          )}

          {progressVisible && (
            <ProgressBar
              id="upload-progress"
              min={0}
              max={100}
              width="30%"
              showStatus={false}
              visible={progressVisible}
              value={progressValue}
            />
          )}

          <FileUploader
            disabled={disabled}
            ref={ref}
            selectButtonText={selectButtonText}
            labelText={labelText}
            uploadMode={uploadMode}
            {...props}
          />

          {infoText && <span className="info-text">{infoText}</span>}
        </div>
      </div>
    </UploadBox>
  )
})
